import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { useAdminAuth } from "@/state/AdminAuthContext";
import {
  adminChangeEmail,
  adminChangePassword,
  adminGetSettings,
} from "@/lib/adminApi";
import { ADMIN_SETTINGS } from "@/constants/testIds/admin";

const genericError = "Permintaan tidak dapat diproses. Sila cuba lagi.";

const errText = (err) => {
  const detail = err?.response?.data?.detail;
  if (err?.response?.status === 429) {
    return "Terlalu banyak percubaan. Sila cuba semula sebentar lagi.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg).filter(Boolean).join(" ") || genericError;
  }
  return genericError;
};

const inputClass =
  "w-full rounded-xl bg-[#0b1f18] text-white placeholder-[#5c7268] ring-1 ring-white/15 focus:ring-2 focus:ring-[#c79a3b] outline-none px-4 py-3 text-sm transition";
const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-[#a9c4b8]";

const Field = ({ id, label, type, value, onChange, testId, autoComplete }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className={labelClass}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      data-testid={testId}
      className={inputClass}
    />
  </div>
);

export default function AdminSettingsPage() {
  const { email, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [currentEmail, setCurrentEmail] = useState(email || "");

  // Change email state
  const [emailPw, setEmailPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  // Change password state
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    adminGetSettings()
      .then((d) => setCurrentEmail(d.email))
      .catch(() => {});
  }, []);

  const forceReauth = async (msg) => {
    toast.success(msg);
    // Server already invalidated all sessions; clear local state and re-login.
    try {
      await logout();
    } catch {
      /* session already gone */
    }
    navigate("/admin/login", { replace: true });
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (emailBusy) return;
    setEmailError("");
    setEmailBusy(true);
    try {
      await adminChangeEmail({
        current_password: emailPw,
        new_email: newEmail.trim(),
      });
      await forceReauth("E-mel admin dikemas kini. Sila log masuk semula.");
    } catch (err) {
      setEmailError(errText(err));
    } finally {
      setEmailBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwBusy) return;
    setPwError("");
    if (newPw !== confirmPw) {
      setPwError("Kata laluan baharu tidak sepadan.");
      return;
    }
    if (newPw.length < 12) {
      setPwError("Kata laluan baharu mesti sekurang-kurangnya 12 aksara.");
      return;
    }
    setPwBusy(true);
    try {
      await adminChangePassword({
        current_password: curPw,
        new_password: newPw,
        confirm_password: confirmPw,
      });
      await forceReauth("Kata laluan dikemas kini. Sila log masuk semula.");
    } catch (err) {
      setPwError(errText(err));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0b1f18] text-white"
      data-testid={ADMIN_SETTINGS.page}
    >
      <AdminHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <h1 className="font-heading text-2xl font-extrabold">Tetapan Admin</h1>

        {/* A. AKAUN ADMIN */}
        <section className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-6">
          <div className="flex items-center gap-2">
            <Mail strokeWidth={1.75} className="h-5 w-5 text-[#c79a3b]" />
            <h2 className="font-heading text-lg font-bold">Akaun Admin</h2>
          </div>
          <p className="mt-3 text-sm text-[#a9c4b8]">
            E-mel Admin semasa:{" "}
            <span
              className="font-semibold text-white"
              data-testid={ADMIN_SETTINGS.currentEmail}
            >
              {currentEmail || "—"}
            </span>
          </p>

          <form onSubmit={submitEmail} className="mt-5 space-y-4">
            <Field
              id="new-email"
              label="E-mel Admin Baharu"
              type="email"
              value={newEmail}
              onChange={setNewEmail}
              autoComplete="off"
              testId={ADMIN_SETTINGS.newEmailInput}
            />
            <Field
              id="email-current-password"
              label="Kata Laluan Semasa"
              type="password"
              value={emailPw}
              onChange={setEmailPw}
              autoComplete="current-password"
              testId={ADMIN_SETTINGS.emailCurrentPasswordInput}
            />
            {emailError && (
              <p
                className="text-sm text-[#ffb4a2] bg-[#3a1512] ring-1 ring-[#ff6b6b]/30 rounded-xl px-3 py-2"
                data-testid={ADMIN_SETTINGS.emailError}
                role="alert"
              >
                {emailError}
              </p>
            )}
            <button
              type="submit"
              disabled={emailBusy}
              data-testid={ADMIN_SETTINGS.changeEmailButton}
              className="inline-flex items-center justify-center gap-2 rounded-xl ring-1 ring-white/15 px-4 py-2.5 text-sm text-[#e8f0ec] hover:bg-white/5 transition disabled:opacity-60"
            >
              {emailBusy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />}
              Tukar E-mel
            </button>
          </form>
        </section>

        {/* B. TUKAR KATA LALUAN */}
        <section className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-6">
          <div className="flex items-center gap-2">
            <KeyRound strokeWidth={1.75} className="h-5 w-5 text-[#c79a3b]" />
            <h2 className="font-heading text-lg font-bold">Tukar Kata Laluan</h2>
          </div>

          <form onSubmit={submitPassword} className="mt-5 space-y-4">
            <Field
              id="current-password"
              label="Kata Laluan Semasa"
              type="password"
              value={curPw}
              onChange={setCurPw}
              autoComplete="current-password"
              testId={ADMIN_SETTINGS.currentPasswordInput}
            />
            <Field
              id="new-password"
              label="Kata Laluan Baharu (min. 12 aksara)"
              type="password"
              value={newPw}
              onChange={setNewPw}
              autoComplete="new-password"
              testId={ADMIN_SETTINGS.newPasswordInput}
            />
            <Field
              id="confirm-password"
              label="Sahkan Kata Laluan Baharu"
              type="password"
              value={confirmPw}
              onChange={setConfirmPw}
              autoComplete="new-password"
              testId={ADMIN_SETTINGS.confirmPasswordInput}
            />
            {pwError && (
              <p
                className="text-sm text-[#ffb4a2] bg-[#3a1512] ring-1 ring-[#ff6b6b]/30 rounded-xl px-3 py-2"
                data-testid={ADMIN_SETTINGS.passwordError}
                role="alert"
              >
                {pwError}
              </p>
            )}
            <button
              type="submit"
              disabled={pwBusy}
              data-testid={ADMIN_SETTINGS.changePasswordButton}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#e0b455] to-[#c79a3b] text-[#0b1f18] font-bold px-4 py-2.5 text-sm transition hover:brightness-105 disabled:opacity-70"
            >
              {pwBusy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />}
              Tukar Kata Laluan
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
